import React, { useContext } from "react";

import { PropTypes } from "prop-types";
import { usePlacesWidget } from "react-google-autocomplete";
import "./GooglePredictionStyle.css";

import { Divider, Typography, FormGroup, Box } from "@mui/material";

import {
  checkIfValueIsEmpty,
  date2String,
  defaultIfEmpty,
  getRandomId,
  string2Date,
} from "../../shared/utils/utils";
import DropDown from "../fields/DropDown";
import MultiSelect from "../fields/MultiSelect";
import Toggle from "../fields/Toggle";
import Checkbox from "../fields/Checkbox";
import DateInput from "../fields/DateInput";
import CharsInput from "../fields/CharsInput";
import NumberInput from "../fields/NumberInput";
import CustomForm from "./CustomForm";
import { getDefaultFormFromTemplate } from "../../shared/utils/form";
import { CustomButton, CustomIconButton } from "../StyledElements";
import { AuthContext } from "../../shared/contexts/AuthContext";

const fieldPropTypes = {
  field: PropTypes.object,
  form: PropTypes.object,
  setForm: PropTypes.func,
  template: PropTypes.arrayOf(PropTypes.object),
  showMissingFields: PropTypes.bool,
  wrap: PropTypes.bool,
};

const onChange = (value, field, setForm, template) => {
  setForm((f) => {
    value = typeof value === "function" ? value(f[field.key], f) : value;
    const newForm = { ...f, [field.key]: value };
    // const additionalUpdate = field.additionalUpdate
    //   ? field.additionalUpdate(field.key, value, f)
    //   : {};
    // Object.assign(newForm, additionalUpdate)
    // set default values for the
    template
      .filter((field) => field.key)
      .filter((field) => field.condition && !field.condition(newForm))
      .forEach((field) => {
        newForm[field.key] = template[field.key].default ?? null;
      });
    return newForm;
  });
};

export const wrapField = (html, name, separator = true) => {
  return (
    <FormGroup>
      {name ? <Typography color="primary">{name}</Typography> : null}
      {html}
      {separator !== false ? (
        <Divider sx={{ bgcolor: "primary.main", marginTop: 2 }} />
      ) : null}
    </FormGroup>
  );
};

wrapField.propTypes = fieldPropTypes;

export const SubtitleField = ({
  field,
  form,
  setForm,
  template,
  showMissingFields,
  wrap = true,
}) => {
  const html = (
    <Typography
      fontSize={field.fontsize || "h6.fontSize"}
      color="primary"
      {...(field.props || {})}
    >
      {field.text}
    </Typography>
  );
  return wrap ? wrapField(html, null, field.separator) : html;
};

SubtitleField.propTypes = fieldPropTypes;

export const ToggleField = ({
  field,
  form,
  setForm,
  template,
  showMissingFields,
  wrap = true,
}) => {
  const isEmpty = field.isEmpty ? field.isEmpty : checkIfValueIsEmpty;
  const html = (
    <Toggle
      disabled={field.disabled}
      onChange={(value) => onChange(value, field, setForm, template)}
      options={
        field.options || [
          { value: true, label: "Ja" },
          { value: false, label: "Nein" },
        ]
      }
      error={!field.optional && showMissingFields && isEmpty(form[field.key])}
      {...(field.props || {})}
    />
  );
  return wrap ? wrapField(html, field.name, field.separator) : html;
};

ToggleField.propTypes = fieldPropTypes;

export const NumberInputField = ({
  field,
  form,
  setForm,
  template,
  showMissingFields,
  wrap = true,
}) => {
  const isEmpty = field.isEmpty ? field.isEmpty : checkIfValueIsEmpty;
  const html = (
    <NumberInput
      value={defaultIfEmpty(form[field.key])}
      disabled={field.disabled}
      name={field.key}
      onChange={(value) => onChange(value, field, setForm, template)}
      error={!field.optional && showMissingFields && isEmpty(form[field.key])}
      {...(field.props || {})}
    />
  );
  return wrap ? wrapField(html, field.name, field.separator) : html;
};

NumberInputField.propTypes = fieldPropTypes;

export const CharsInputField = ({
  field,
  form,
  setForm,
  template,
  showMissingFields,
  wrap = true,
}) => {
  const isEmpty = field.isEmpty ? field.isEmpty : checkIfValueIsEmpty;
  const html = (
    <CharsInput
      label={field.name}
      value={field.key ? defaultIfEmpty(form[field.key]) : field.value}
      disabled={!field.key || field.disabled}
      name={field.key}
      onChange={(value) => onChange(value, field, setForm, template)}
      error={!field.optional && showMissingFields && isEmpty(form[field.key])}
      {...(field.props || {})}
    />
  );
  return wrap ? wrapField(html, null, field.separator) : html;
};

CharsInputField.propTypes = fieldPropTypes;

export const GoogleAddressInputField = ({
  field,
  form,
  setForm,
  template,
  showMissingFields,
  wrap = true,
}) => {
  const { creds } = useContext(AuthContext);
  const isEmpty = field.isEmpty ? field.isEmpty : checkIfValueIsEmpty;
  const { ref: materialRef } = usePlacesWidget({
    apiKey: creds?.google_api_key,
    onPlaceSelected: (place) => {
      const streetAndNumber = place.formatted_address.split(",")[0];
      const zipAndCity = place.formatted_address.split(",")[1].substring(1);
      if (field.selectAddress) {
        field.selectAddress(
          { street_and_number: streetAndNumber, zip_and_city: zipAndCity },
          form,
          setForm,
        );
      }
    },
    options: {
      componentRestrictions: { country: "de" },
      types: ["address"],
      fields: ["name", "formatted_address", "address_components"],
    },
  });
  const html = (
    <CharsInput
      value={defaultIfEmpty(form[field.key])}
      disabled={field.disabled}
      label={field.name}
      name={"street_and_number"}
      onChange={(value) => onChange(value, field, setForm, template)}
      error={!field.optional && showMissingFields && isEmpty(form[field.key])}
      inputRef={materialRef}
      autoComplete={true}
      {...(field.props || {})}
    />
  );
  return wrap ? wrapField(html, null, field.separator) : html;
};

GoogleAddressInputField.propTypes = fieldPropTypes;

export const CheckBoxField = ({
  field,
  form,
  setForm,
  template,
  showMissingFields,
  wrap = true,
}) => {
  const isEmpty = field.isEmpty ? field.isEmpty : checkIfValueIsEmpty;
  const html = (
    <Checkbox
      label={field.name}
      disabled={field.disabled}
      onChange={(checked) => onChange(checked, field, setForm, template)}
      checked={!!form[field.key]}
      error={!field.optional && showMissingFields && isEmpty(form[field.key])}
      {...(field.props || {})}
    />
  );
  return wrap ? wrapField(html, null, field.separator) : html;
};

CheckBoxField.propTypes = fieldPropTypes;

export const DropdownField = ({
  field,
  form,
  setForm,
  template,
  showMissingFields,
  wrap = true,
}) => {
  const isEmpty = field.isEmpty ? field.isEmpty : checkIfValueIsEmpty;
  const html = (
    <DropDown
      onChange={(value) => onChange(value, field, setForm, template)}
      label={field.name}
      options={field.options}
      value={form[field.key]}
      disabled={field.disabled}
      error={!field.optional && showMissingFields && isEmpty(form[field.key])}
      {...(field.props || {})}
    />
  );

  return wrap ? wrapField(html, null, field.separator) : html;
};

DropdownField.propTypes = fieldPropTypes;

export const MultiSelectField = ({
  field,
  form,
  setForm,
  template,
  showMissingFields,
  wrap = true,
}) => {
  const isEmpty = field.isEmpty ? field.isEmpty : checkIfValueIsEmpty;
  const html = (
    <MultiSelect
      text={field.text || field.name}
      sort={true}
      options={field.options}
      values={form[field.key]}
      disabled={field.disabled}
      onChange={(values) => onChange(values, field, setForm, template)}
      error={!field.optional && showMissingFields && isEmpty(form[field.key])}
      {...(field.props || {})}
    />
  );
  return wrap ? wrapField(html, field.name, field.separator) : html;
};

MultiSelectField.propTypes = fieldPropTypes;

export const DateField = ({
  field,
  form,
  setForm,
  template,
  showMissingFields,
  wrap = true,
}) => {
  const isEmpty = field.isEmpty ? field.isEmpty : checkIfValueIsEmpty;
  const html = (
    <DateInput
      disabled={field.disabled}
      value={form[field.key] ? string2Date(form[field.key]) : null}
      error={!field.optional && showMissingFields && isEmpty(form[field.key])}
      onChange={(date) =>
        onChange(date ? date2String(date) : null, field, setForm, template)
      }
      {...(field.props || {})}
    />
  );
  return wrap ? wrapField(html, field.name, field.separator) : html;
};

DateField.propTypes = fieldPropTypes;

export const CustomField = ({
  field,
  form,
  setForm,
  template,
  showMissingFields,
  wrap = true,
}) => {
  const isEmpty = field.isEmpty ? field.isEmpty : checkIfValueIsEmpty;
  if (!field.getFieldHtml) return null;
  const html = (
    <>
      {field.getFieldHtml(
        form,
        setForm,
        showMissingFields,
        field.key ? isEmpty(form[field.key], true) : false,
      )}
    </>
  );
  return wrap ? wrapField(html, field.name, field.separator) : html;
};

CustomField.propTypes = fieldPropTypes;

export const FormListField = ({
  field,
  form,
  setForm,
  template,
  showMissingFields,
  wrap = true,
}) => {
  const subFormTemplate = field.template || {};
  const disabled = field.disabled;

  const deleteItem = (itemId) => {
    onChange(
      (items) => items.filter((i) => i.id !== itemId),
      field,
      setForm,
      template,
    );
  };

  const updateItem = (itemId, update) => {
    onChange(
      (items) => {
        const newItems = [...items];
        const item = newItems.find((x) => x.id === itemId);
        update = typeof update === "function" ? update(item) : update;
        Object.assign(item, update);
        return newItems;
      },
      field,
      setForm,
      template,
    );
  };

  const addItem = () => {
    onChange(
      (items) => {
        return [
          ...(items || []),
          {
            id: -getRandomId(),
            ...getDefaultFormFromTemplate(subFormTemplate),
          },
        ];
      },
      field,
      setForm,
      template,
    );
  };

  const html = (
    <Box sx={{ marginLeft: "50px" }}>
      <Divider sx={{ bgcolor: "primary.main", marginTop: 2 }} />
      {(form[field.key] || []).map((item) => (
        <Box key={`item_${item.id}`}>
          {disabled ? null : (
            <CustomIconButton
              onClick={() => deleteItem(item.id)}
              style={{ color: "#efefef", float: "right" }}
              icon="bin"
            />
          )}
          <CustomForm
            template={subFormTemplate.map((r) => ({
              ...r,
              disabled: r.disabled || disabled,
            }))}
            form={item}
            setForm={(f) => {
              updateItem(item.id, f);
            }}
            showMissingFields={showMissingFields}
          />
          <Divider sx={{ bgcolor: "primary.main", marginTop: 2 }} />
        </Box>
      ))}
      {disabled ? null : (
        <CustomButton
          onClick={() => addItem()}
          style={{ width: "200px" }}
          icon="add"
          id="add-item-btn"
        >
          {field.addBtnName || "Hizufügen"}
        </CustomButton>
      )}
    </Box>
  );

  return wrap ? wrapField(html, field.name, field.separator) : html;
};

FormListField.propTypes = fieldPropTypes;
